import React from 'react';
import { Ticket, RefreshCw, User, Trash2, AlertCircle } from 'lucide-react';
import { useAdminTicketsPage } from './useAdminTicketsPage';
import AdminTabNav from '../AdminPage/AdminTabNav';
import AdminPagination from '../AdminPage/AdminPagination';
import AdminTicketsFilters from './AdminTicketsFilters';
import AdminTicketResponseModal from './AdminTicketResponseModal';
import AdminTicketDeleteModal from './AdminTicketDeleteModal';
import './AdminTicketsPage.css';

const AdminTicketsPage: React.FC = () => {
  const {
    filteredTickets,
    paginatedTickets,
    currentPage,
    setCurrentPage,
    totalPages,
    itemsPerPage,
    loading,
    error,
    statusFilter,
    setStatusFilter,
    priorityFilter,
    setPriorityFilter,
    issueTypeFilter,
    setIssueTypeFilter,
    selectedTicket,
    setSelectedTicket,
    adminResponse,
    setAdminResponse,
    updating,
    deleteTarget,
    setDeleteTarget,
    deleting,
    loadTickets,
    getFilenameFromUrl,
    getStatusCount,
    getPriorityCount,
    getIssueTypeCount,
    formatIssueType,
    handleUpdateStatus,
    handleUpdateResponse,
    handleDeleteTicket,
    getStatusBadge,
    getPriorityBadge,
  } = useAdminTicketsPage();

  return (
    <div className="admin-tickets-page">
      <div className="admin-tickets-page-bg">
        <div className="admin-tickets-page-bg-1" />
        <div className="admin-tickets-page-bg-2" />
      </div>
      <div className="admin-tickets-page-inner">
        <AdminTabNav />
        <div className="admin-tickets-header">
          <div>
            <h1 className="admin-tickets-title">Tickets Management</h1>
            <p className="admin-tickets-subtitle">Manage and respond to support tickets</p>
          </div>
          <button
            type="button"
            onClick={loadTickets}
            disabled={loading}
            className="admin-tickets-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {error && (
          <div className="admin-tickets-error">
            <AlertCircle className="w-4 h-4 mr-2" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <AdminTicketsFilters
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          priorityFilter={priorityFilter}
          setPriorityFilter={setPriorityFilter}
          issueTypeFilter={issueTypeFilter}
          setIssueTypeFilter={setIssueTypeFilter}
          getStatusCount={getStatusCount}
          getPriorityCount={getPriorityCount}
          getIssueTypeCount={getIssueTypeCount}
        />

        {loading ? (
          <div className="admin-tickets-loading">
            <div className="admin-tickets-loading-spinner" />
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="admin-tickets-empty">
            <Ticket className="w-8 h-8 text-gray-300 mx-auto mb-2 block" />
            <p className="text-gray-400 text-xs">No tickets found</p>
          </div>
        ) : (
          <>
          <div className="admin-tickets-list">
            {paginatedTickets.map((ticket) => {
              const statusBadge = getStatusBadge(ticket.status);
              const priorityBadge = getPriorityBadge(ticket.priority);
              const PriorityIcon = priorityBadge.icon;
              return (
                <div key={ticket.id} className="admin-tickets-card">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-white truncate">{ticket.title}</h3>
                          <div className="flex items-center space-x-1.5 text-xs text-gray-400 mt-0.5">
                            <User className="w-2.5 h-2.5" />
                            <span>{ticket.user_email}</span>
                            <span>•</span>
                            {ticket.issue_type && (
                              <>
                                <span>{formatIssueType(ticket.issue_type)}</span>
                                <span>•</span>
                              </>
                            )}
                            <span>
                              {new Date(ticket.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                          <span
                            className={`px-1.5 py-0.5 rounded text-xs font-medium border flex items-center space-x-1 ${priorityBadge.bg} ${priorityBadge.text} ${priorityBadge.border}`}
                          >
                            <PriorityIcon className="w-2.5 h-2.5" />
                            <span className="capitalize">{ticket.priority}</span>
                          </span>
                          <span
                            className={`px-1.5 py-0.5 rounded text-xs font-medium border ${statusBadge.bg} ${statusBadge.text} ${statusBadge.border}`}
                          >
                            {ticket.status.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-300 mb-2 line-clamp-2">{ticket.description}</p>
                      {ticket.image_url && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {ticket.image_url
                            .split(',')
                            .map((url) => url.trim())
                            .filter(
                              (url) =>
                                url &&
                                (url.startsWith('http://') ||
                                  url.startsWith('https://') ||
                                  url.startsWith('/api/'))
                            )
                            .map((url, index) => {
                              const imageUrl = url.startsWith('/api/')
                                ? `${process.env.REACT_APP_API_URL || ''}${url}`
                                : url;
                              const fileName = getFilenameFromUrl(imageUrl);
                              return (
                                <div key={index} className="inline-block">
                                  <img
                                    src={imageUrl}
                                    alt={`Ticket attachment ${index + 1}`}
                                    className="max-w-[200px] h-auto rounded border border-gray-700/50 max-h-24 object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                    onClick={() => window.open(imageUrl, '_blank', 'noopener,noreferrer')}
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                  />
                                  <p className="text-xs text-gray-400 mt-1 truncate max-w-[200px]" title={fileName}>
                                    {fileName}
                                  </p>
                                </div>
                              );
                            })}
                        </div>
                      )}
                      {ticket.admin_response && (
                        <div className="mt-2 pt-2 border-t border-gray-700/50">
                          <p className="text-xs text-gray-400 mb-0.5">Admin Response:</p>
                          <p className="text-xs text-gray-300 line-clamp-2">{ticket.admin_response}</p>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-gray-700/50">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setAdminResponse(ticket.admin_response || '');
                          }}
                          className="px-2 py-1 text-xs bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20 rounded transition-colors"
                        >
                          Respond
                        </button>
                        {ticket.status === 'open' && (
                          <button
                            type="button"
                            onClick={() => handleUpdateStatus(ticket.id, 'in_progress')}
                            disabled={updating}
                            className="px-2 py-1 text-xs bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20 rounded transition-colors disabled:opacity-50"
                          >
                            In Progress
                          </button>
                        )}
                        {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
                          <button
                            type="button"
                            onClick={() => handleUpdateStatus(ticket.id, 'resolved')}
                            disabled={updating}
                            className="px-2 py-1 text-xs bg-green-500/10 hover:bg-green-500/20 text-green-400 border border-green-500/20 rounded transition-colors disabled:opacity-50"
                          >
                            Resolve
                          </button>
                        )}
                        {ticket.status !== 'closed' && (
                          <button
                            type="button"
                            onClick={() => handleUpdateStatus(ticket.id, 'closed')}
                            disabled={updating}
                            className="px-2 py-1 text-xs bg-gray-500/10 hover:bg-gray-500/20 text-gray-400 border border-gray-500/20 rounded transition-colors disabled:opacity-50"
                          >
                            Close
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(ticket)}
                          className="px-2 py-1 text-xs bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded transition-colors flex items-center space-x-1"
                        >
                          <Trash2 className="w-2.5 h-2.5" />
                          <span>Delete</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {filteredTickets.length > 0 && (
            <AdminPagination
              currentPage={Math.min(currentPage, totalPages)}
              totalPages={totalPages}
              totalItems={filteredTickets.length}
              itemsPerPage={itemsPerPage}
              itemLabel="tickets"
              onPageChange={setCurrentPage}
            />
          )}
          </>
        )}

        {deleteTarget && (
          <AdminTicketDeleteModal
            ticket={deleteTarget}
            onClose={() => setDeleteTarget(null)}
            onConfirm={handleDeleteTicket}
            deleting={deleting}
          />
        )}

        {selectedTicket && (
          <AdminTicketResponseModal
            ticket={selectedTicket}
            adminResponse={adminResponse}
            onAdminResponseChange={setAdminResponse}
            onClose={() => {
              setSelectedTicket(null);
              setAdminResponse('');
            }}
            onSave={handleUpdateResponse}
            updating={updating}
            getFilenameFromUrl={getFilenameFromUrl}
          />
        )}
      </div>
    </div>
  );
};

export default AdminTicketsPage;
